import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useRemoveBonus } from 'server';
import { useFetchBalance } from 'js/hooks/user';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { useSaveScroll } from '../useSaveScroll';
import { useTranslations } from '@core/js/hooks/i18n';
import { dispatcher } from 'dispatcher';
import { useShowPopupWithClass } from '../useShowPopupWithClass';
import { REMOVE_BONUS_POPUP_ERROR_TITLE } from '../useShowPopupWithClass/constants';

type Actions = {
    toggleLoading: (arg: boolean) => void;
    setError: (error: { error: string }) => void;
    updateData?: () => void;
}

type UseMyPromotionsCancelBonusType = { actions: Actions; onSuccess?: () => void; key: string };

export const useMyPromotionsCancelBonus = ({ actions, onSuccess, key }: UseMyPromotionsCancelBonusType) => {
    const isMounted = useIsMounted();
    const fetchBalance = useFetchBalance();
    const dispatch = useDispatch();
    const removeBonus = useRemoveBonus();
    const translate = useTranslations();
    const { restoreScroll } = useSaveScroll(key);
    const content = translate(REMOVE_BONUS_POPUP_ERROR_TITLE);
    const showPopup = useShowPopupWithClass();

    return useCallback((bonusInstanceCode: string): void => {
        dispatch(actions.toggleLoading(true));
        dispatch(actions.setError({ error: '' }));

        (async () => {
            try {
                await removeBonus(bonusInstanceCode);
                await fetchBalance();

                if (onSuccess) {
                    onSuccess();
                }
                dispatcher.dispatch('notification-bonusCounter:updated');
                isMounted() && actions?.updateData && dispatch(actions?.updateData());
            } catch (error) {
                isMounted() && showPopup({
                    className: 'bonus-cancel-popup',
                    content
                });

            } finally {
                isMounted() && dispatch(actions.toggleLoading(false));
                restoreScroll();
            }
        })();
    }, [
        actions, content, dispatch,
        fetchBalance, isMounted, onSuccess,
        removeBonus, restoreScroll, showPopup
    ]);
};
